import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { queryClient } from '@/core/queryClient';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  appId: string;
  mustChangePassword: boolean;
}

export interface LoginData {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  /** Tenant slug used to authenticate. Persisted so flows like first-setup can re-login. */
  appSlug?: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  /** Tenant slug used at login (e.g. "pymestock-default"); needed to re-login. */
  appSlug: string | null;
  isAuthenticated: boolean;
  login: (data: LoginData) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      appSlug: null,
      isAuthenticated: false,

      // El cache de TanStack Query se vacia en cada cambio de sesion. Las query keys
      // no incluyen el tenant (son ['categories'], ['products', filtros], etc.), asi
      // que sin esto una sesion nueva puede renderizar datos cacheados del tenant
      // anterior: staleTime es de 5 min, o sea que ni siquiera se dispara un refetch.
      // Es la unica barrera del lado del cliente — la API ya filtra bien por AppId.
      login: (data: LoginData) => {
        queryClient.clear();
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
          appSlug: data.appSlug ?? null,
          isAuthenticated: true,
        });
      },

      logout: () => {
        queryClient.clear();
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          appSlug: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'pymestock-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist tokens, user and tenant slug — isAuthenticated is derived on rehydration
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        appSlug: state.appSlug,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
